import React from 'react';
import styled from 'styled-components';
import { Callout, FlexBox, Icon, Typography, typographyPresets } from '@agilepoint/wat-react-ux';
import { NotificationDetailsContainer } from 'modules/core/containers';
import { NotificationDetailsAdminContainer } from 'modules/core/containers/notification-details-admin-container';
import { useConfigScopeSelectors } from 'modules/core/scopes';
import { Localize } from '@agilepoint/wat-react';

interface INotificationCountProps {
    notificationCount: number;
    isMenuCollapsed: boolean;
}

const NotificationContent = styled(FlexBox)`
    gap: 8px;
    cursor: pointer;
`;

const NotificationWrapper = styled(FlexBox) <{ isMenuCollapsed: boolean, animateNotification: boolean }>`
    position: relative;
    width: 40px;
    height: 40px;
    text-align: center;
    cursor: pointer;
    gap: 0;
    padding-left: ${(props) => (props.isMenuCollapsed ? '6px' : '0')};

    @keyframes zoomNotification {
        from {
            transform: scale3d(1, 1, 1);
        }
        10%, 20% {
            transform: scale3d(.9, .9, .9) rotate3d(0, 0, 1, -3deg);
        }
        30%, 50%, 70%, 90% {
            transform: scale3d(1.1, 1.1, 1.1) rotate3d(0, 0, 1, 3deg);
        }
        40%, 60%, 80% {
            transform: scale3d(1.1, 1.1, 1.1) rotate3d(0, 0, 1, -3deg);
        }
        to {
            transform: scale3d(1, 1, 1);
        }
    }
    
    animation: ${props => (props.animateNotification ? 'zoomNotification 3s infinite' : 'none')};
`;

const Count = styled.div`
    height: 20px;
    width: 20px;
    position: absolute;
    background-color: ${(props) => props.theme.mobileAppAccelerator.semanticColors.notificationBadgeColor};
    color: white;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 1rem;
    ${typographyPresets.headings.h6};
    bottom: 20px;
    right: 6px;
`;

const StyledIcon = styled(Icon)`
    fill: ${(props) => props.theme.mobileAppAccelerator.semanticColors.grayVariant3};
`;

const StyledCallout = styled(Callout)`
    .ap-callout {
        width: 300px;
        height: 400px;
    }
`;

export function NotificationCount(props: INotificationCountProps) {
    const { notificationCount, isMenuCollapsed } = props;
    const [showCallout, setShowCallout] = React.useState(false);
    const [showAnimation, setShowAnimation] = React.useState(notificationCount > 0);

    const notificationRef = React.createRef<HTMLDivElement>();

    const { isUserTypeAdmin } = useConfigScopeSelectors();
    const isAdminTypeUser = isUserTypeAdmin();

    const showHideCalloutHandler = () => {
        setShowCallout(p => !p);
        setShowAnimation(false);
    };

    return (
        <>
            <NotificationContent alignItems="center" ref={notificationRef} onClick={showHideCalloutHandler}>
                <NotificationWrapper isMenuCollapsed={isMenuCollapsed} animateNotification={showAnimation} alignItems="center">
                    <StyledIcon iconName='ap-announcement.outline' iconSize='24' />
                    <Count>{notificationCount}</Count>
                </NotificationWrapper>
                {!isMenuCollapsed && <Typography.Medium title=''><Localize translate='modules.userActions.notifications.label' /></Typography.Medium>}
            </NotificationContent>
            {showCallout && (
                <StyledCallout
                    onDismiss={showHideCalloutHandler}
                    targetRef={notificationRef}
                    preventClickEventPropagation
                    isBeakVisible beakWidth={10}>
                    {
                        isAdminTypeUser ? <NotificationDetailsAdminContainer showHideCallout={showHideCalloutHandler} /> :
                            <NotificationDetailsContainer showHideCallout={showHideCalloutHandler} />
                    }
                </StyledCallout>
            )}
        </>
    );
}
